import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./serv.css";
import Calculator from "../../common/calculator";
import { useDispatch, useSelector } from "react-redux";
import { fetchLoanDetails } from "../../store/slices/loanDetailsSlice";
import Loader from "../../layout/Loader";

export default function Services({ slug, title }) {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("");
  const [visibleBanksCount, setVisibleBanksCount] = useState(24);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();
  const sectionRefs = useRef({});
  const STORAGE_URL = process.env.REACT_APP_URL;

  const tabMenu = [
    { label: "Features", key: "features", icon: "✨" },
    { label: "Eligibility", key: "eligibility_criteria", icon: "✅" },
    { label: "Documents", key: "documents", icon: "📄" },
    { label: "Fees", key: "fees_charges", icon: "💳" },
    { label: "Policy", key: "loan_policy", icon: "📜" },
    { label: "Calculator", key: "emi_calculator", icon: "🧮" },
    { label: "Banks", key: "available_banks", icon: "🏦" },
  ];

  const { loanData, error } = useSelector((state) => state.loanDetails);

  // ✅ API CALL
  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchLoanDetails({ slug }));
  }, [dispatch, slug]);

  // ✅ LOADER STOP
  useEffect(() => {
    if (loanData || error) {
      setIsLoading(false);
    }
  }, [loanData, error]);

  useEffect(() => {
    if (!loanData) return;

    const available = tabMenu.find(
      (tab) =>
        tab.key === "emi_calculator" ||
        (tab.key === "available_banks" && loanData.banks?.length > 0) ||
        (loanData[tab.key] && loanData[tab.key] !== ""),
    );

    if (available) setActiveTab(available.label);
  }, [loanData]);

  // ✅ SCROLL OBSERVER
  useEffect(() => {
    if (!loanData) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute("id");
            const tab = tabMenu.find((t) => t.key === id);
            if (tab) setActiveTab(tab.label);
          }
        });
      },
      {
        root: null,
        rootMargin: "-20% 0px -70% 0px",
        threshold: 0,
      },
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [loanData]);

  const visibleTabs = tabMenu.filter(
    (tab) =>
      tab.key === "emi_calculator" ||
      (tab.key === "available_banks" && loanData?.banks?.length > 0) ||
      (loanData && loanData[tab.key] && loanData[tab.key] !== ""),
  );

  const handleTabClick = (label, key) => {
    setActiveTab(label);
    const element = sectionRefs.current[key];

    if (element) {
      const offset = 100;
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;

      window.scrollTo({
        top: elementPosition - offset,
        behavior: "smooth",
      });
    }
  };

  // ✅ ONLY YOUR LOADER
  if (isLoading) return <Loader />;

  // ✅ ERROR / EMPTY
  if (error) return <div className="ln-pulse-error">Error: {error}</div>;
  if (!loanData)
    return <div className="ln-pulse-error">Service data unavailable.</div>;

  return (
    <div className="ln-pulse-master-container">
      {/* Header */}
      <header className="ln-pulse-hero">
        <div className="ln-pulse-hero-content">
          <h1 className="ln-pulse-main-title">
            {loanData.sub_service_name || title}
          </h1>
          <p className="ln-pulse-subtitle">
            Explore customized rates and transparent policies for your needs.
          </p>
        </div>
      </header>

      <div className="ln-pulse-layout">
        {/* Sidebar */}
        <aside className="ln-pulse-sidebar">
          <nav className="ln-pulse-nav">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                className={`ln-pulse-nav-btn ${
                  activeTab === tab.label ? "active" : ""
                }`}
                onClick={() => handleTabClick(tab.label, tab.key)}
              >
                <span className="ln-pulse-nav-icon">{tab.icon}</span>
                <span className="ln-pulse-nav-label">{tab.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="ln-pulse-content">
          {visibleTabs.map((tab) => (
            <section
              key={tab.key}
              id={tab.key}
              className="ln-pulse-card-section"
              ref={(el) => (sectionRefs.current[tab.key] = el)}
            >
              <div className="ln-pulse-card-header">
                <h2 className="ln-pulse-card-title">{tab.label}</h2>
                <div className="ln-pulse-divider"></div>
              </div>

              <div className="ln-pulse-card-body">
                {tab.key === "emi_calculator" ? (
                  <div className="ln-pulse-calc-wrapper">
                    <h2>{title}</h2>
                    <Calculator
                      minRate={parseFloat(loanData?.interest_rate_min) || 8}
                      maxRate={parseFloat(loanData?.interest_rate_max) || 20}
                      minTenure={parseInt(loanData?.tenure_min) || 1}
                      maxTenure={parseInt(loanData?.tenure_max) || 30}
                      tenureUnit={loanData?.tenure_unit || "years"}
                      minAmount={parseInt(loanData?.loan_amount_min) || 100000}
                      maxAmount={
                        parseInt(loanData?.loan_amount_max) || 50000000
                      }
                    />
                  </div>
                ) : tab.key === "available_banks" ? (
                  <div className="ln-pulse-banks-area">
                    <div className="ln-pulse-banks-grid">
                      {loanData.banks
                        .slice(0, visibleBanksCount)
                        .map((bank, i) => (
                          <div key={i} className="ln-pulse-bank-item">
                            <img
                              src={
                                bank.bank_logo
                                  ? `${STORAGE_URL}/storage/${bank.bank_logo}`
                                  : "/images/default-bank.png"
                              }
                              alt="bank"
                              className="ln-pulse-bank-img"
                            />
                          </div>
                        ))}
                    </div>

                    {loanData.banks.length > visibleBanksCount && (
                      <button
                        className="ln-pulse-more-btn lms-load-more-container apply-btn"
                        onClick={() => setVisibleBanksCount((v) => v + 21)}
                      >
                        Load More
                      </button>
                    )}
                  </div>
                ) : (
                  <div
                    className="ln-pulse-rich-text"
                    dangerouslySetInnerHTML={{
                      __html: loanData[tab.key],
                    }}
                  />
                )}
              </div>
            </section>
          ))}
        </main>
      </div>
    </div>
  );
}
